import { garantirTokenValido, renovarSessao } from '@/utils/upavetSessao';
import { DATA_NAO_INFORMADA, formatarDataBR, formatarDataHoraBR } from '@/utils/datas';

/**
 * Camada de dados da área UPA VET — conforme o Contrato de Uso da API (v2.0).
 *
 * Arquitetura:
 *   - Autenticação: Supabase Auth (mesma sessão do Portal/Console).
 *   - Dados: PostgREST na VPS (`VITE_PUBLIC_API_URL`). O JWT do Supabase
 *     (`session.access_token`) é aceito DIRETAMENTE pelo PostgREST — não há mais
 *     Edge Function `petbea-token-exchange` (eliminada na v2.0).
 *     Toda requisição leva `Authorization: Bearer <access_token>`,
 *     `Accept: application/json` e `Prefer: count=exact` (paginação via Content-Range).
 *   - O `access_token` é mantido em memória e sincronizado via `onAuthStateChange`
 *     (seção 4 do contrato) — ver `@/utils/upavetSessao`.
 *   - Renovação automática: antes de cada requisição o token é conferido e renovado
 *     PROATIVAMENTE se estiver perto de expirar (margem de segurança), para o usuário
 *     não tomar 401 no meio do uso. Se ainda assim a API responder 401, a sessão é
 *     renovada e a requisição repetida uma vez, conforme a seção 9 do contrato.
 *
 * Enquanto um endpoint não estiver publicado, a consulta falha de forma tratada e a
 * UI mostra o estado de "indisponível" com opção de tentar novamente.
 */

/** Linha genérica cujas colunas não são fixadas no front. */
export type UpavetLinha = Record<string, unknown>;

export interface UpavetLista<T> {
  linhas: T[];
  total: number;
}

export interface KpisGerais {
  total_atendimentos: number;
  total_tutores: number;
  total_animais: number;
  faturamento_total: number;
  ticket_medio: number;
  ticket_mediano: number;
  competencias_com_dados: number;
  castracoes: number;
  microchips: number;
  eutanasias: number;
  /** Contagem deduplicada de animais atendidos (nome real exposto pela view). */
  animais_unicos?: number;
  /** Contagem deduplicada de tutores/responsáveis (nome real exposto pela view). */
  tutores_unicos?: number;
  /** Média já calculada pela view: `faturamento_total` ÷ `animais_unicos`. */
  faturamento_por_animal?: number;
  /** Média já calculada pela view: `faturamento_total` ÷ `tutores_unicos`. */
  faturamento_por_tutor?: number;
}

export interface SerieTemporal {
  competencia: string;
  atendimentos: number;
  faturamento: number;
  tutores_ativos: number;
  animais_atendidos: number;
}

export interface HospitalBenchmark {
  hospital: string;
  cidade: string;
  uf: string;
  atendimentos_referencia: number;
  faturamento_referencia: number;
  valor_anualizado: number;
  annualization_factor: number;
  dias_periodo_real: number;
}

export interface QualidadeDados {
  indicador: string;
  valor: number | string;
  categoria: string;
}

export interface TutorUnico {
  tutor_id: number | string;
  nome: string;
  cpf: string | null;
  /** Pode vir com mais de um número no mesmo campo (ex.: "6235784116 62992081924"). */
  telefone?: string | null;
  /** Telefone secundário do tutor. */
  telefone2?: string | null;
  /** E-mail do tutor. */
  email?: string | null;
  /** Cidade do tutor. */
  municipio?: string | null;
  /** Bairro do tutor. */
  bairro?: string | null;
  /** CEP do tutor. */
  cep?: string | null;
  /** Flag da view: tutor sem nenhum telefone cadastrado. */
  sem_telefone?: boolean | null;
  /** Flag da view: tutor sem endereço cadastrado. */
  sem_endereco?: boolean | null;
  variantes_nome: string | null;
  total_atendimentos: number;
  faturamento_gerado: number;
  tem_duplicata_por_cpf: boolean;
  eh_registro_principal: boolean;
  registros_com_mesmo_cpf: number;
  ultimo_atendimento: string | null;
}

export interface AnimalUnico {
  animal_id: number | string;
  nome: string;
  especie: string;
  microchip: string | null;
  tutor_principal: string | null;
  total_atendimentos: number;
  tem_duplicata: boolean;
  sem_microchip: boolean;
  sem_raca: boolean;
  sem_data_nascimento: boolean;
  classificacao_continuidade: string;
}

export interface Duplicata {
  entidade: string;
  motivo: string;
  nome_a: string;
  id_a: number | string;
  nome_b: string;
  id_b: number | string;
  chave_comum: string | null;
}

export interface Healthcheck {
  status: string;
  checked_at: string;
  ultima_carga_at?: string;
  horas_desde_ultima_carga?: number;
  etl_ok?: boolean;
  conflitos_abertos?: number;
  mvs_populadas?: number;
  mvs_total?: number;
  total_atendimentos?: number;
  total_tutores?: number;
  total_animais?: number;
  schema_version?: number;
}

/* ------------------------------------------------------------------ */
/* Núcleo: acesso ao PostgREST da VPS                                 */
/* ------------------------------------------------------------------ */

const API_URL = ((import.meta.env.VITE_PUBLIC_API_URL as string) || '').replace(/\/+$/, '');

/** Erro tratado da API da UPA VET (carrega o status HTTP quando disponível). */
export class UpavetApiError extends Error {
  status: number;

  constructor(mensagem: string, status = 0) {
    super(mensagem);
    this.name = 'UpavetApiError';
    this.status = status;
  }
}

/**
 * true quando o erro é um 403 — ou seja, falta de PERMISSÃO (usuário sem acesso).
 * Importante: 403 NÃO é sessão expirada. Nunca deve disparar refresh nem logout.
 */
export function ehErroDePermissao(e: unknown): boolean {
  return e instanceof UpavetApiError && e.status === 403;
}

/* Núcleo de sessão/token e renovação automática: `@/utils/upavetSessao`. */

/** Dispara um GET no PostgREST com o token informado. */
async function requisitar(url: string, token: string | null, contar: boolean): Promise<Response> {
  try {
    return await fetch(url, {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        Accept: 'application/json',
        ...(contar ? { Prefer: 'count=exact' } : {}),
      },
    });
  } catch {
    throw new UpavetApiError(
      'Não foi possível conectar ao serviço de dados da UPA VET. Verifique a rede e tente novamente.',
    );
  }
}

interface OpcoesBusca {
  select?: string;
  order?: string;
  limit?: number;
  offset?: number;
  eq?: Record<string, string | number | boolean>;
  ilike?: Record<string, string>;
  isNull?: string[];
  naoNulo?: string[];
  gt?: Record<string, number>;
  contar?: boolean;
}

/** Monta os parâmetros de query no formato PostgREST. */
function paramsDeOpcoes(opcoes: OpcoesBusca): Record<string, string> {
  const params: Record<string, string> = {};
  if (opcoes.select) params.select = opcoes.select;
  if (opcoes.order) params.order = opcoes.order;
  if (typeof opcoes.limit === 'number') params.limit = String(opcoes.limit);
  if (typeof opcoes.offset === 'number') params.offset = String(opcoes.offset);
  if (opcoes.eq) {
    for (const [campo, valor] of Object.entries(opcoes.eq)) {
      params[campo] = `eq.${valor === true ? 'true' : valor === false ? 'false' : valor}`;
    }
  }
  if (opcoes.gt) {
    for (const [campo, valor] of Object.entries(opcoes.gt)) params[campo] = `gt.${valor}`;
  }
  if (opcoes.ilike) {
    for (const [campo, valor] of Object.entries(opcoes.ilike)) params[campo] = `ilike.*${valor}*`;
  }
  if (opcoes.isNull) {
    for (const campo of opcoes.isNull) params[campo] = 'is.null';
  }
  if (opcoes.naoNulo) {
    for (const campo of opcoes.naoNulo) params[campo] = 'not.is.null';
  }
  return params;
}

/** Executa uma requisição GET no PostgREST da VPS e devolve linhas + total. */
async function fetchAPI<T>(
  caminho: string,
  params: Record<string, string> = {},
  contar = true,
): Promise<UpavetLista<T>> {
  if (!API_URL) {
    throw new UpavetApiError(
      'A URL da API da UPA VET não está configurada (VITE_PUBLIC_API_URL).',
    );
  }

  const url = new URL(`${API_URL}/${caminho.replace(/^\/+/, '')}`);
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, v);
  });
  const endpoint = url.toString();

  // Renovação proativa: entrega um token válido (renova antes de expirar), evitando
  // o 401 no meio do uso.
  let token = await garantirTokenValido();
  let res = await requisitar(endpoint, token, contar);

  // Contrato v2.0 (seção 9): em 401, renova a sessão e repete a requisição uma vez.
  if (res.status === 401) {
    const renovado = await renovarSessao();
    if (renovado) {
      token = renovado;
      res = await requisitar(endpoint, token, contar);
    }
  }

  if (res.status === 401) {
    throw new UpavetApiError(
      'Sua sessão expirou. Entre novamente no Console para consultar a base UPA VET.',
      401,
    );
  }
  if (res.status === 406) {
    throw new UpavetApiError(
      'A base UPA VET recusou a requisição por falta do cabeçalho Accept: application/json.',
      406,
    );
  }
  if (res.status === 403) {
    // Falta de PERMISSÃO (e-mail fora da lista de administradores) — não é erro de sessão.
    throw new UpavetApiError('Seu usuário não tem acesso a esta área.', 403);
  }
  if (!res.ok) {
    let msg = `A base UPA VET respondeu com status ${res.status}.`;
    if (res.status === 404) {
      msg = 'Este endpoint ainda não está publicado na base UPA VET. Assim que estiver disponível, os dados aparecem aqui automaticamente.';
    } else if (res.status === 400) {
      msg = 'Os filtros enviados à base UPA VET são inválidos.';
    } else if (res.status === 503) {
      msg = 'A API da UPA VET está temporariamente indisponível. Consulte o healthcheck da base.';
    } else if (res.status >= 500) {
      msg = 'A base UPA VET apresentou um erro interno. Tente novamente em instantes.';
    } else {
      try {
        const corpo = await res.json();
        if (corpo?.message) msg = corpo.message;
      } catch {
        /* corpo não-JSON: mantém a mensagem padrão */
      }
    }
    throw new UpavetApiError(msg, res.status);
  }

  const linhas = ((await res.json()) as T[]) ?? [];
  const contentRange = res.headers.get('Content-Range');
  const totalBruto = contentRange && contentRange.includes('/') ? Number(contentRange.split('/')[1]) : NaN;
  const total = Number.isFinite(totalBruto) ? totalBruto : linhas.length;
  return { linhas, total };
}

async function buscar<T>(caminho: string, opcoes: OpcoesBusca = {}): Promise<UpavetLista<T>> {
  const params = paramsDeOpcoes(opcoes);
  return fetchAPI<T>(caminho, params, opcoes.contar !== false);
}

/* ------------------------------------------------------------------ */
/* KPIs e Operação                                                    */
/* ------------------------------------------------------------------ */

export async function fetchKpisGerais(): Promise<KpisGerais | null> {
  const { linhas } = await buscar<KpisGerais>('v_kpis_gerais', { limit: 1 });
  return linhas[0] ?? null;
}

/* ------------------------------------------------------------------ */
/* Relatório Executivo — view consolidada `v_relatorio_executivo`      */
/* ------------------------------------------------------------------ */

/**
 * A view `v_relatorio_executivo` devolve um ARRAY com uma ÚNICA linha
 * contendo TODAS as seções do Relatório Executivo em JSON. Usamos `resposta[0]`.
 *
 * Regra de honestidade: `kpis.valor_referencia` é o VALOR DE REFERÊNCIA dos
 * serviços registrados — não é faturamento nem receita contábil.
 */

export interface RelatorioExecutivoPeriodo {
  inicio: string;
  fim: string;
  meses_completos: number;
  ultimo_mes_completo: string;
  ultima_competencia: string;
  ultima_competencia_parcial: boolean;
}

export interface RelatorioExecutivoKpis {
  comandas: number;
  animais_unicos: number;
  tutores_unicos: number;
  procedimentos: number;
  lancamentos: number;
  valor_referencia: number;
  castracoes: number;
  microchips: number;
  eutanasias: number;
  comandas_sem_valor: number;
  comandas_sem_valor_pct: number;
}

export interface MediaMediana {
  media: number;
  mediana: number;
}

export interface RelatorioExecutivoMediasMensais {
  comandas: MediaMediana;
  tutores: MediaMediana;
  animais: MediaMediana;
  procedimentos: MediaMediana;
  valor: MediaMediana;
  castracoes: MediaMediana;
  microchips: MediaMediana;
  eutanasias: MediaMediana;
}

export interface PorComandaProcedimentos {
  media: number;
  mediana: number;
  p75: number;
}

export interface PorComandaLancamentos {
  media: number;
  mediana: number;
}

export interface PorComandaValor {
  comandas: number;
  media: number;
  mediana: number;
  p25: number;
  p75: number;
}

export interface RelatorioExecutivoPorComanda {
  procedimentos: PorComandaProcedimentos;
  lancamentos: PorComandaLancamentos;
  valor_com_valor: PorComandaValor;
}

export interface IndicadorComparativo {
  indicador: string;
  de: number;
  ate: number;
  variacao_pct: number;
}

export interface RelatorioExecutivoComparativoAno {
  de: string;
  ate: string;
  indicadores: IndicadorComparativo[];
}

export interface IndicadorMesParcial {
  indicador: string;
  anterior: number;
  atual: number;
  variacao_pct: number;
}

export interface RelatorioExecutivoMesParcial {
  competencia: string;
  comparado_com: string;
  ate_dia: number;
  indicadores: IndicadorMesParcial[];
}

export interface PicoExecutivo {
  valor: number;
  competencia: string;
}

export interface RelatorioExecutivoPicos {
  comandas: PicoExecutivo;
  animais: PicoExecutivo;
  procedimentos: PicoExecutivo;
  valor: PicoExecutivo;
  castracoes: PicoExecutivo;
  microchips: PicoExecutivo;
  procedimentos_por_comanda: PicoExecutivo;
}

export interface CastracaoPorTipo {
  tipo: string;
  quantidade: number;
  participacao_pct: number;
}

export interface RelatorioExecutivoContinuidade {
  animais: number;
  animais_mais_de_uma_comanda: number;
  taxa_mais_de_uma_comanda_pct: number;
  animais_mais_de_uma_competencia: number;
  taxa_mais_de_uma_competencia_pct: number;
  media_comandas_por_animal: number;
  max_comandas_por_animal: number;
}

export interface RelatorioExecutivoQualidade {
  comandas_sem_valor: number;
  comandas_sem_animal: number;
  comandas_sem_data: number;
  animais_mais_de_um_tutor: number;
  itens_quantidade_fracionaria: number;
  lancamentos: number;
  quantidades: number;
}

export interface RelatorioExecutivoCadastro {
  media_animais_por_tutor: number;
  mediana_animais_por_tutor: number;
  max_animais_por_tutor: number;
}

export interface PerfilAssistencialItem {
  categoria: string;
  lancamentos: number;
  quantidade: number;
  valor: number;
  participacao_valor_pct: number;
}

export interface SerieMensalItem {
  competencia: string;
  rotulo: string;
  completo: boolean;
  comandas: number;
  tutores: number;
  animais: number;
  procedimentos: number;
  lancamentos: number;
  valor: number;
  comandas_sem_valor: number;
  castracoes: number;
  microchips: number;
  eutanasias: number;
  procedimentos_por_comanda: number;
}

export interface RelatorioExecutivo {
  periodo: RelatorioExecutivoPeriodo;
  kpis: RelatorioExecutivoKpis;
  medias_mensais: RelatorioExecutivoMediasMensais;
  por_comanda: RelatorioExecutivoPorComanda;
  comparativo_ano: RelatorioExecutivoComparativoAno;
  /** `null` quando o último mês está completo — nesse caso a seção deve ser ocultada. */
  mes_parcial: RelatorioExecutivoMesParcial | null;
  picos: RelatorioExecutivoPicos;
  castracoes_por_tipo: CastracaoPorTipo[];
  continuidade: RelatorioExecutivoContinuidade;
  qualidade: RelatorioExecutivoQualidade;
  cadastro: RelatorioExecutivoCadastro;
  perfil_assistencial: PerfilAssistencialItem[];
  serie_mensal: SerieMensalItem[];
  calculado_em: string;
}

/** Busca a linha única do Relatório Executivo (view `v_relatorio_executivo`). */
export async function fetchRelatorioExecutivo(): Promise<RelatorioExecutivo | null> {
  const { linhas } = await buscar<RelatorioExecutivo>('v_relatorio_executivo', {
    limit: 1,
    contar: false,
  });
  return linhas[0] ?? null;
}

/* ------------------------------------------------------------------ */
/* Relatório de Castrações — view consolidada `v_relatorio_castracoes` */
/* ------------------------------------------------------------------ */

/**
 * A view `v_relatorio_castracoes` devolve um ARRAY com uma ÚNICA linha
 * contendo TODAS as seções do Relatório de Castrações em JSON. Usamos `resposta[0]`.
 * Os valores batem linha a linha com o PDF (13 meses, saldo acumulado, participação,
 * total acumulado, média de mês completo, pico e o ano corrente).
 */

export interface RelatorioCastracoesPeriodo {
  inicio: string;
  fim: string;
  /** Rótulos (ex.: "Set/2025") dos meses que não fecharam. */
  meses_parciais: string[];
}

export interface RelatorioCastracoesPico {
  valor: number;
  competencia: string;
}

export interface RelatorioCastracoesAnoAtual {
  ano: number;
  total: number;
  ate: string;
  participacao_pct: number;
}

export interface RelatorioCastracoesResumo {
  total_acumulado: number;
  media_mes_completo: number;
  meses_completos: number;
  pico: RelatorioCastracoesPico;
  ano_atual: RelatorioCastracoesAnoAtual;
}

export interface RelatorioCastracoesPorAno {
  ano: number;
  total: number;
  participacao_pct: number;
  desde: string;
  ate: string;
  completo: boolean;
}

export interface CastracoesPorTipoMes {
  canino_femea: number;
  canino_macho: number;
  felino_femea: number;
  felino_macho: number;
  outros: number;
}

export interface RelatorioCastracoesLote {
  id: string;
  arquivo: string;
  periodo_inicio: string;
  periodo_fim: string;
  importado_em: string;
}

export interface RelatorioCastracoesMes {
  competencia: string;
  rotulo: string;
  parcial: boolean;
  castracoes: number;
  saldo_acumulado: number;
  participacao_pct: number;
  por_tipo: CastracoesPorTipoMes;
  lote: RelatorioCastracoesLote | null;
}

export interface RelatorioCastracoesTotal {
  castracoes: number;
  saldo_acumulado: number;
  participacao_pct: number;
}

export interface RelatorioCastracoesTipoItem {
  tipo: string;
  quantidade: number;
  participacao_pct: number;
}

export interface RelatorioCastracoes {
  periodo: RelatorioCastracoesPeriodo;
  resumo: RelatorioCastracoesResumo;
  por_ano: RelatorioCastracoesPorAno[];
  meses: RelatorioCastracoesMes[];
  total: RelatorioCastracoesTotal;
  /** Distribuição consolidada no período (rótulos "Fêmea Canino" etc.). */
  por_tipo: RelatorioCastracoesTipoItem[];
  visao_geral: string;
  leituras: string[];
  nota_metodologica: string;
  calculado_em: string;
}

/** Busca a linha única do Relatório de Castrações (view `v_relatorio_castracoes`). */
export async function fetchRelatorioCastracoes(): Promise<RelatorioCastracoes | null> {
  const { linhas } = await buscar<RelatorioCastracoes>('v_relatorio_castracoes', {
    limit: 1,
    contar: false,
  });
  return linhas[0] ?? null;
}

export async function fetchSerieTemporal(limite = 12): Promise<SerieTemporal[]> {
  const { linhas } = await buscar<SerieTemporal>('v_serie_temporal', {
    order: 'competencia.desc',
    limit: limite,
  });
  return linhas;
}

export async function fetchFaturamentoAcumulado(limite = 24): Promise<UpavetLinha[]> {
  const { linhas } = await buscar<UpavetLinha>('v_faturamento_acumulado', { limit: limite });
  return linhas;
}

/** Nomes de coluna candidatos a representar o "valor" monetário de uma linha da UPA VET. */
const CANDIDATOS_COLUNA_VALOR = [
  'faturamento_total',
  'valor_total',
  'total_valor',
  'faturamento',
  'valor',
  'receita',
  'custo_total',
];

/** Lê uma coluna numérica da linha; devolve 0 quando ausente ou inválida. */
export function valorNumerico(linha: UpavetLinha, chave: string | null | undefined): number {
  if (!chave) return 0;
  const n = Number(linha[chave]);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Descobre qual coluna representa o valor monetário da linha: tenta os nomes
 * conhecidos e, na falta deles, a primeira coluna numérica "de grandeza"
 * (ignorando identificadores).
 */
export function detectarColunaValor(linhas: UpavetLinha[]): string | null {
  if (linhas.length === 0) return null;
  const chaves = Object.keys(linhas[0]);
  for (const c of CANDIDATOS_COLUNA_VALOR) {
    if (chaves.includes(c)) return c;
  }
  for (const chave of chaves) {
    if (/(^|_)id$/.test(chave)) continue;
    const valores = linhas.map((l) => l[chave]);
    const numerica = valores.every((v) => v === null || v === undefined || typeof v === 'number');
    const temValor = valores.some((v) => typeof v === 'number' && Number(v) !== 0);
    if (numerica && temValor) return chave;
  }
  return null;
}

/**
 * Ranking de procedimentos por valor. A view `v_procedimentos_ranking` não expõe
 * a coluna `total_valor` (nome histórico inválido) — por isso tentamos o nome
 * mais provável e, se a ordenação for recusada, buscamos sem ordenar e
 * ordenamos no cliente pela coluna de valor detectada. Assim a seção nunca
 * cai no estado de "base indisponível" por causa do nome da coluna.
 */
export async function fetchProcedimentos(limite = 100): Promise<UpavetLinha[]> {
  try {
    const { linhas } = await buscar<UpavetLinha>('v_procedimentos_ranking', {
      order: 'faturamento_total.desc',
      limit: limite,
    });
    return linhas;
  } catch {
    const { linhas } = await buscar<UpavetLinha>('v_procedimentos_ranking', { limit: limite });
    const coluna = detectarColunaValor(linhas);
    return [...linhas].sort((a, b) => valorNumerico(b, coluna) - valorNumerico(a, coluna));
  }
}

/** Procedimentos por categoria — colunas confirmadas pela API. */
export interface ProcedimentoCategoria {
  categoria: string;
  faturamento_total: number;
  pct_faturamento: number;
  total_procedimentos: number;
  total_qty: number;
  /** Contagem deduplicada de animais atendidos na categoria (quando a base expõe). */
  animais_unicos?: number;
  /** Contagem deduplicada de tutores/responsáveis na categoria (quando a base expõe). */
  tutores_unicos?: number;
}

export async function fetchProcedimentosPorCategoria(): Promise<ProcedimentoCategoria[]> {
  const { linhas } = await buscar<ProcedimentoCategoria>('v_procedimentos_por_categoria', { limit: 100 });
  return linhas;
}

/* ------------------------------------------------------------------ */
/* Índice de incidência — view `v_procedimentos_incidencia`            */
/* ------------------------------------------------------------------ */

/**
 * Uma linha da `v_procedimentos_incidencia`.
 *
 * A view é "muito longa": traz categorias e procedimentos no MESMO conjunto,
 * separados por `nivel` (`categoria` | `procedimento`). `competencia` nula
 * representa o período COMPLETO (todo o histórico); quando preenchida, é o mês
 * de referência (dia 1º). O nome do item depende do nível — para categorias vem
 * em `categoria`; para procedimentos no nome canônico (detectado no front).
 */
export interface ProcedimentoIncidencia {
  nivel: string;
  categoria: string | null;
  competencia: string | null;
  posicao: number;
  /** % dos atendimentos em que o item aparece. */
  incidencia_pct: number;
  /** Quantos atendimentos tinham o item (numerador da incidência). */
  atendimentos_com: number;
  /** Total de atendimentos do período (denominador da incidência). */
  total_atendimentos: number;
  /** Média calculada SÓ nos atendimentos em que o item aparece. */
  quantidade_media_por_atendimento: number;
  /** Valor médio calculado SÓ nos atendimentos em que o item aparece. */
  valor_medio_por_atendimento: number;
  /** true marca mês incompleto (parcial). */
  parcial: boolean;
  [k: string]: unknown;
}

/**
 * Busca a incidência por nível, no período completo (`competencia = null`) ou em
 * um mês específico. A `posicao` já vem ordenada por incidência.
 */
export async function fetchIncidencia(
  nivel: 'categoria' | 'procedimento',
  competencia?: string | null,
): Promise<ProcedimentoIncidencia[]> {
  const { linhas } = await buscar<ProcedimentoIncidencia>('v_procedimentos_incidencia', {
    eq: { nivel, ...(competencia ? { competencia } : {}) },
    isNull: competencia ? undefined : ['competencia'],
    order: 'posicao.asc',
    limit: 300,
  });
  return linhas;
}

/** Meses (competências) disponíveis na view, com marcação de mês parcial. */
export interface CompetenciaIncidencia {
  competencia: string;
  parcial: boolean;
}

/**
 * Descobre os meses disponíveis na view. Consulta leve (só `competencia,parcial`)
 * no nível de categoria, com `competencia` preenchida, e deduplica no cliente.
 */
export async function fetchIncidenciaCompetencias(): Promise<CompetenciaIncidencia[]> {
  const { linhas } = await buscar<{ competencia: string | null; parcial: boolean | null }>(
    'v_procedimentos_incidencia',
    {
      select: 'competencia,parcial',
      eq: { nivel: 'categoria' },
      naoNulo: ['competencia'],
      order: 'competencia.desc',
      limit: 500,
      contar: false,
    },
  );
  const mapa = new Map<string, boolean>();
  for (const l of linhas) {
    const comp = l.competencia;
    if (!comp) continue;
    mapa.set(comp, (mapa.get(comp) ?? false) || Boolean(l.parcial));
  }
  return [...mapa.entries()].map(([competencia, parcial]) => ({ competencia, parcial }));
}

/** Evolução mensal da incidência de UMA categoria (competência crescente). */
export async function fetchIncidenciaEvolucaoCategoria(
  categoria: string,
): Promise<ProcedimentoIncidencia[]> {
  const { linhas } = await buscar<ProcedimentoIncidencia>('v_procedimentos_incidencia', {
    eq: { nivel: 'categoria', categoria },
    naoNulo: ['competencia'],
    order: 'competencia.asc',
    limit: 200,
    contar: false,
  });
  return linhas;
}

export async function fetchCastrometro(): Promise<UpavetLinha[]> {
  const { linhas } = await buscar<UpavetLinha>('v_castrometro', {
    order: 'competencia.desc',
    limit: 120,
  });
  return linhas;
}

export async function fetchCastrometroPivot(ano?: number): Promise<UpavetLinha[]> {
  const { linhas } = await buscar<UpavetLinha>('v_castrometro_pivot', {
    eq: ano !== undefined ? { ano } : undefined,
    order: 'competencia.asc',
    limit: 120,
  });
  return linhas;
}

/**
 * Descobre quais anos existem na `v_castrometro_pivot` para alimentar o seletor
 * de ano do Panorama consolidado. Busca apenas a coluna `ano` (consulta leve) e
 * devolve os anos únicos em ordem decrescente.
 */
export async function fetchCastrometroPivotAnos(): Promise<number[]> {
  const { linhas } = await buscar<{ ano: number }>('v_castrometro_pivot', {
    select: 'ano',
    limit: 500,
    contar: false,
  });
  const anos = new Set<number>();
  for (const linha of linhas) {
    const valor = Number(linha.ano);
    if (Number.isFinite(valor)) anos.add(valor);
  }
  return [...anos].sort((a, b) => b - a);
}

/* ------------------------------------------------------------------ */
/* Tutores e Animais                                                  */
/* ------------------------------------------------------------------ */

export async function fetchAnimaisPerfil(): Promise<UpavetLinha[]> {
  const { linhas } = await buscar<UpavetLinha>('v_animais_perfil', { limit: 200 });
  return linhas;
}

export async function fetchTutoresTop(limite = 20): Promise<UpavetLinha[]> {
  const { linhas } = await buscar<UpavetLinha>('v_tutores_top', { limit: limite });
  return linhas;
}

export async function fetchAnimaisRecorrencia(): Promise<UpavetLinha[]> {
  const { linhas } = await buscar<UpavetLinha>('v_animais_recorrencia_detalhes', {
    eq: { classificacao_continuidade: 'LONGITUDINAL' },
    limit: 200,
  });
  return linhas;
}

/* ------------------------------------------------------------------ */
/* Benchmark e Qualidade                                              */
/* ------------------------------------------------------------------ */

export async function fetchHospitalBenchmark(): Promise<HospitalBenchmark[]> {
  const { linhas } = await buscar<HospitalBenchmark>('v_hospital_benchmark', { limit: 100 });
  return linhas;
}

export async function fetchQualidadeDados(): Promise<UpavetLinha[]> {
  const { linhas } = await buscar<UpavetLinha>('v_qualidade_dados', { limit: 100 });
  return linhas;
}

export async function fetchReconciliacao(): Promise<UpavetLinha[]> {
  const { linhas } = await buscar<UpavetLinha>('v_reconciliacao', { limit: 200 });
  return linhas;
}

/* ------------------------------------------------------------------ */
/* ETL e Auditoria                                                    */
/* ------------------------------------------------------------------ */

/**
 * Status de um lote de importação (view `v_batch_status`).
 *
 * Cada linha representa um arquivo XLS processado pela ETL da UPA VET, com o ciclo
 * de vida (`draft` → `published` ou `rolled_back`), a competência coberta, os totais
 * vinculados e o resumo do que foi inserido (`domain_delta` / `integrity_report`).
 * O `batch_id` é o identificador usado nas ações de publicar/desfazer.
 */
export interface LoteImportacaoStatus {
  batch_id: string;
  filename_original: string | null;
  publish_status: string;
  status: string | null;
  imported_by: string | null;
  imported_at: string;
  competencia_ano: number | null;
  competencia_mes: number | null;
  is_complete: boolean | null;
  completion_pct: number | null;
  total_encounters: number | null;
  total_items: number | null;
  conflitos_pendentes: number | null;
  criticos_pendentes: number | null;
  domain_delta: Record<string, unknown> | null;
  integrity_report: Record<string, unknown> | null;
}

export interface OpcoesLotesImportacao {
  limit?: number;
  offset?: number;
  /** Quando true, devolve só os lotes que já têm atendimentos vinculados (`total_encounters > 0`). */
  somenteComDados?: boolean;
}

/**
 * Lista os lotes importados da UPA VET (view `v_batch_status`), restritos ao
 * namespace da UPAVET de Goiânia, do mais recente para o mais antigo.
 */
export async function fetchLotesImportacao(
  opcoes: OpcoesLotesImportacao = {},
): Promise<UpavetLista<LoteImportacaoStatus>> {
  return buscar<LoteImportacaoStatus>('v_batch_status', {
    eq: { source_namespace: 'GOIANIA:UPAVET' },
    gt: opcoes.somenteComDados ? { total_encounters: 0 } : undefined,
    order: 'imported_at.desc',
    limit: opcoes.limit ?? 50,
    offset: opcoes.offset ?? 0,
  });
}

export async function fetchQuarantine(): Promise<UpavetLinha[]> {
  const { linhas } = await buscar<UpavetLinha>('v_quarantine', {
    isNull: ['resolved_at'],
    limit: 100,
  });
  return linhas;
}

/* ------------------------------------------------------------------ */
/* Healthcheck (acesso público)                                       */
/* ------------------------------------------------------------------ */

export async function fetchHealthcheck(): Promise<Healthcheck | null> {
  const { linhas } = await fetchAPI<Healthcheck>('v_healthcheck', {}, false);
  return linhas[0] ?? null;
}

/* ------------------------------------------------------------------ */
/* Admin — importação e conciliação                                  */
/* ------------------------------------------------------------------ */

export interface FiltroAdmin {
  termo?: string;
  duplicata?: boolean;
  semTelefone?: boolean;
  semEndereco?: boolean;
  semMicrochip?: boolean;
  offset?: number;
  limit?: number;
}

export async function fetchAdminTutores(opcoes: FiltroAdmin = {}): Promise<UpavetLista<TutorUnico>> {
  const eq: Record<string, string | number | boolean> = {};
  if (opcoes.duplicata) eq.tem_duplicata_por_cpf = true;
  // Filtros por flags de cadastro (PostgREST: ?sem_telefone=eq.true e ?sem_endereco=eq.true).
  if (opcoes.semTelefone) eq.sem_telefone = true;
  if (opcoes.semEndereco) eq.sem_endereco = true;
  return buscar<TutorUnico>('v_admin_tutores_unicos', {
    order: 'ultimo_atendimento.desc',
    limit: opcoes.limit ?? 25,
    offset: opcoes.offset ?? 0,
    eq,
    ilike: opcoes.termo ? { nome: opcoes.termo } : undefined,
  });
}

export async function fetchAdminAnimais(opcoes: FiltroAdmin = {}): Promise<UpavetLista<AnimalUnico>> {
  const eq: Record<string, string | number | boolean> = {};
  if (opcoes.duplicata) eq.tem_duplicata = true;
  if (opcoes.semMicrochip) eq.sem_microchip = true;
  return buscar<AnimalUnico>('v_admin_animais_unicos', {
    order: 'total_atendimentos.desc',
    limit: opcoes.limit ?? 25,
    offset: opcoes.offset ?? 0,
    eq,
    ilike: opcoes.termo ? { nome: opcoes.termo } : undefined,
  });
}

export async function fetchAdminTutorAnimais(tutorId: string | number): Promise<UpavetLinha[]> {
  const { linhas } = await buscar<UpavetLinha>('v_admin_tutor_animais', {
    eq: { tutor_id: tutorId },
    limit: 200,
  });
  return linhas;
}

export async function fetchAdminDuplicatas(entidade?: string): Promise<Duplicata[]> {
  const { linhas } = await buscar<Duplicata>('v_admin_duplicatas', {
    eq: entidade ? { entidade } : undefined,
    limit: 300,
  });
  return linhas;
}

/* ------------------------------------------------------------------ */
/* Admin — verificação de acesso às views restritas                   */
/* ------------------------------------------------------------------ */

/** Resultado da sonda de acesso às views administrativas (v_admin_*). */
export type AcessoAdminUpavet = 'autorizado' | 'negado' | 'indisponivel';

/**
 * Sonda o acesso às views administrativas do UPA VET pela checagem por e-mail
 * feita no JWT (GET /v_admin_tutores_unicos?limit=1):
 *   200 → autorizado · 403 → negado (e-mail fora da lista de administradores).
 *
 * O 403 é PERMISSÃO, nunca sessão: não renova token nem desloga. Qualquer outro
 * resultado (sem rede, 401, 5xx) fica como "indisponivel" para não esconder o
 * menu por engano — só escondemos quando a própria API confirma o bloqueio.
 */
export async function verificarAcessoAdmin(): Promise<AcessoAdminUpavet> {
  try {
    await buscar<UpavetLinha>('v_admin_tutores_unicos', { limit: 1, contar: false });
    return 'autorizado';
  } catch (e) {
    if (ehErroDePermissao(e)) return 'negado';
    return 'indisponivel';
  }
}

/* ------------------------------------------------------------------ */
/* Formatação                                                        */
/* ------------------------------------------------------------------ */

export function formatarMoeda(valor: unknown): string {
  // Ausente não é zero: Number(null) daria 0 e a tela mostraria "R$ 0,00".
  if (valor === null || valor === undefined || valor === '') return '—';
  const n = typeof valor === 'number' ? valor : Number(valor);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export function formatarNumero(valor: unknown): string {
  if (valor === null || valor === undefined || valor === '') return '—';
  const n = typeof valor === 'number' ? valor : Number(valor);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('pt-BR');
}

export function formatarData(valor: unknown): string {
  if (!valor) return '—';
  const texto = valor instanceof Date ? valor.toISOString() : String(valor);
  const f = formatarDataHoraBR(texto);
  return f === DATA_NAO_INFORMADA ? texto : f;
}

/**
 * Converte um texto em Title Case, preservando preposições minúsculas.
 * Os nomes vêm em CAIXA ALTA do banco (ex.: "COSTELINHA MARQUES" → "Costelinha Marques",
 * "BIFE PEREIRA" → "Bife Pereira").
 */
export function tituloCase(valor: unknown): string {
  const texto = String(valor ?? '').trim();
  if (!texto) return '—';
  const minusculas = new Set(['de', 'da', 'do', 'das', 'dos', 'e', 'di', 'du', 'del', 'van', 'von']);
  return texto
    .toLocaleLowerCase('pt-BR')
    .split(/\s+/)
    .map((palavra, indice) => {
      if (indice > 0 && minusculas.has(palavra)) return palavra;
      return palavra.charAt(0).toLocaleUpperCase('pt-BR') + palavra.slice(1);
    })
    .join(' ');
}

/**
 * Formata um ou mais telefones brasileiros contidos no mesmo campo.
 * Aceita múltiplos números separados por espaço, vírgula ou barra
 * (ex.: "6235784116 62992081924" → "(62) 3578-4116 / (62) 99208-1924").
 */
export function formatarTelefones(valor: unknown): string {
  if (valor === null || valor === undefined || valor === '') return '—';
  const partes = String(valor)
    .split(/[\s,;/|]+/)
    .map((parte) => parte.replace(/\D/g, ''))
    .filter((digitos) => digitos.length >= 8);
  if (partes.length === 0) return String(valor);
  return partes
    .map((digitos) => {
      if (digitos.length === 11) return `(${digitos.slice(0, 2)}) ${digitos.slice(2, 7)}-${digitos.slice(7)}`;
      if (digitos.length === 10) return `(${digitos.slice(0, 2)}) ${digitos.slice(2, 6)}-${digitos.slice(6)}`;
      if (digitos.length === 9) return `${digitos.slice(0, 5)}-${digitos.slice(5)}`;
      if (digitos.length === 8) return `${digitos.slice(0, 4)}-${digitos.slice(4)}`;
      return digitos;
    })
    .join(' / ');
}

/**
 * Encurta identificadores longos (UUID/hex) para exibição em tabelas.
 * Valores curtos (números, ids pequenos) são mantidos por inteiro.
 * ex.: "d09e452a-4605-5fcf-a4a4-67260da81e73" → "d09e452a…"
 */
export function encurtarId(valor: unknown): string {
  const texto = String(valor ?? '').trim();
  if (!texto) return '—';
  if (texto.length <= 12) return texto;
  return `${texto.slice(0, 8)}…`;
}

/** Formata uma data sem hora, para listagens (nascimento, vínculo etc.). */
export function formatarDataCurta(valor: unknown): string {
  if (!valor) return '—';
  const texto = valor instanceof Date ? valor.toISOString() : String(valor);
  const f = formatarDataBR(texto);
  return f === DATA_NAO_INFORMADA ? texto : f;
}

/** Copia um texto para a área de transferência, com fallback para navegadores antigos. */
export async function copiarTexto(texto: string): Promise<boolean> {
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(texto);
      return true;
    }
  } catch {
    /* segue para o fallback abaixo */
  }
  try {
    const el = document.createElement('textarea');
    el.value = texto;
    el.style.position = 'fixed';
    el.style.opacity = '0';
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
    return true;
  } catch {
    return false;
  }
}

export function rotularColuna(chave: string): string {
  return chave.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Mascara um CPF para exibição/exportação nas listagens de admin,
 * conforme o contrato: nunca expor o documento completo, apenas os 4 últimos dígitos.
 * Aceita CPF já formatado ou apenas os dígitos. Valores não reconhecidos são
 * mascarados por completo (evita vazamento acidental).
 */
export function mascararCpf(cpf: unknown): string {
  if (cpf === null || cpf === undefined || cpf === '') return '—';
  const digitos = String(cpf).replace(/\D/g, '');
  if (digitos.length !== 11) {
    if (digitos.length === 0) return '—';
    if (digitos.length < 4) return '•'.repeat(digitos.length);
    return `${'•'.repeat(digitos.length - 4)}${digitos.slice(-4)}`;
  }
  // Mantém visíveis apenas os 4 últimos dígitos: ***.**.*XX-XX
  return `***.**.*${digitos.slice(7, 9)}-${digitos.slice(9, 11)}`;
}